/**
 * Municípios de uma empresa, dentro do detalhe da empresa — Nexora Grid (frontend, US-1903).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 1.1.0
 *
 * Cadastro estrutural do integrador da Nexora (ADR-0014): o código é a dimensão `municipio` do tópico MQTT e não muda;
 * nome, UF, IBGE (busca na lista oficial embutida), filial e exibição no CCO se corrigem com a versão lida. Excluir só
 * sem dados ligados; com dados, tire do CCO. Os vistos na telemetria sem cadastro aparecem para cadastrar.
 * v1.1 (US-4604, Sprint 46): bloco com título e contagem, tabela densa no bloco, formulário em grupos com validação ao
 * sair do campo; mesmas chamadas e permissões.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Campo, Esqueleto, Selo } from "../componentes/Tela";
import "./estrutura.css";

type Municipio = { codigo: string; nome: string; uf: string; ibge: string; filial: string; simulado: boolean; exibir_cco: boolean;
  cadastrado: boolean; versao: number | null; dispositivos: number; unidades: number };
type Ibge = { ibge: string; nome: string; uf: string };
type Form = { codigo: string; nome: string; uf: string; ibge: string; filial: string; exibir_cco: boolean; simulado: boolean; versao: number | null };
const VAZIO: Form = { codigo: "", nome: "", uf: "", ibge: "", filial: "", exibir_cco: true, simulado: false, versao: null };
type Campos = "codigo" | "nome" | "uf" | "ibge";

/** Mensagem de cada campo, as mesmas regras dos atributos do formulário (pattern, maxLength, required). */
function erroDe(f: Form, c: Campos): string | null {
  if (c === "codigo") return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(f.codigo) ? null : "Use letras minúsculas, números, - ou _ (ex.: costarica).";
  if (c === "nome") return f.nome.trim() ? null : "Escreva o nome do município.";
  if (c === "uf") return !f.uf || /^[A-Z]{2}$/.test(f.uf) ? null : "Duas letras (ex.: MS).";
  return !f.ibge || /^[0-9]{7}$/.test(f.ibge) ? null : "Sete números; use \"Buscar no IBGE\".";
}

export function Municipios({ empresa, edita, aoMudar }: { empresa: string; edita: boolean; aoMudar?: () => void }) {
  const [lista, setLista] = useState<Municipio[] | null>(null);
  const [filiais, setFiliais] = useState<string[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [tocados, setTocados] = useState<Set<Campos>>(new Set());
  const [busca, setBusca] = useState<Ibge[]>([]);
  const carregar = useCallback(() => {
    api<{ municipios: Municipio[] }>(`/v1/municipios?empresa=${empresa}`).then((r) => setLista(r.municipios)).catch(() => setLista([]));
    api<{ filiais: { codigo: string }[] }>(`/v1/admin/empresas/${empresa}/filiais`)
      .then((r) => setFiliais(r.filiais.map((f) => f.codigo))).catch(() => setFiliais([]));
  }, [empresa]);
  useEffect(carregar, [carregar]);
  const abrirForm = (f: Form | null) => { setForm(f); setTocados(new Set()); setBusca([]); };
  const concluir = (ok: string) => { setAviso(ok); abrirForm(null); carregar(); aoMudar?.(); };
  const falhar = (e: unknown) => setAviso(e instanceof ErroApi ? `Não foi possível: ${e.message}` : "Falha de rede.");
  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const { codigo, versao, ...dados } = form;
    try {
      await api(`/v1/municipios/${codigo}`, { metodo: "PUT", corpo: { empresa, ...dados, ...(versao ? { versao } : {}) } });
      concluir(`Município ${dados.nome} salvo.`);
    } catch (x) { falhar(x); }
  };
  const excluir = async (m: Municipio) => {
    try { await api(`/v1/municipios/${m.codigo}?empresa=${empresa}`, { metodo: "DELETE" }); concluir("Município excluído."); }
    catch (x) { falhar(x); }
  };
  const buscarIbge = async () => {
    if (!form || form.nome.trim().length < 2) return;
    try { setBusca(await api<Ibge[]>(`/v1/municipios/ibge?nome=${encodeURIComponent(form.nome.trim())}${form.uf ? `&uf=${form.uf}` : ""}`)); }
    catch (x) { falhar(x); }
  };
  const editar = (m: Municipio) => abrirForm({ codigo: m.codigo, nome: m.cadastrado ? m.nome : "", uf: m.uf, ibge: m.ibge, filial: m.filial,
    exibir_cco: m.exibir_cco, simulado: m.simulado, versao: m.versao });
  const tocar = (c: Campos) => setTocados((t) => new Set(t).add(c));
  const erro = (c: Campos) => (form && tocados.has(c) ? erroDe(form, c) : null);
  const cadastrados = lista?.filter((m) => m.cadastrado).length ?? 0;
  const semCadastro = (lista?.length ?? 0) - cadastrados;
  return (
    <section className="filiais tl-bloco admin-secao" aria-label="Municípios">
      <header>
        <h3>Municípios</h3>
        {lista && <span className="tl-subtitulo">{cadastrados} cadastrados{semCadastro ? ` · ${semCadastro} sem cadastro` : ""}</span>}
        {edita && !form && <div className="tl-acoes"><button type="button" className="nx-botao" onClick={() => abrirForm(VAZIO)}>Novo município</button></div>}
      </header>
      {aviso && <p className="admin-aviso" role="status">{aviso}</p>}
      {!lista ? <Esqueleto linhas={3} /> : (
        <>
          <div className="admin-tabela">
            <table className="tl-tabela">
              <thead><tr><th scope="col">Código</th><th scope="col">Nome</th><th scope="col">IBGE</th><th scope="col">Filial</th><th scope="col">No CCO</th>
                <th scope="col">Dispositivos (24 h)</th>{edita && <th scope="col"><span className="visualmente-oculto">Ações</span></th>}</tr></thead>
              <tbody>
                {lista.map((m) => (
                  <tr key={m.codigo}>
                    <td className="tl-mono">{m.codigo}</td>
                    <td>{m.cadastrado ? `${m.nome}${m.uf ? `/${m.uf}` : ""}` : <Selo tom="ruim">sem cadastro</Selo>}
                      {m.simulado && <small className="nx-suave"> · simulação</small>}</td>
                    <td className="tl-mono">{m.ibge || "—"}</td><td>{m.filial || "—"}</td>
                    <td>{m.exibir_cco ? "sim" : "não"}</td><td className="tl-mono">{m.dispositivos}</td>
                    {edita && (
                      <td className="acoes-linha">
                        <button type="button" className="tl-fantasma botao-linha" onClick={() => editar(m)}>{m.cadastrado ? "Editar" : "Cadastrar"}</button>
                        {m.cadastrado && !m.dispositivos && (
                          <button type="button" className="tl-fantasma botao-linha" onClick={() => void excluir(m)}>Excluir</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!lista.length && <tr><td colSpan={7} className="vazio">Nenhum município{edita ? ": use \"Novo município\"." : "."}</td></tr>}
              </tbody>
            </table>
          </div>
          {edita && form && (
            <div className="admin-corpo">
              <form className="form-linha" onSubmit={(e) => void salvar(e)} aria-label="Município">
                <Campo id="mun-codigo" rotulo="Código do município" erro={erro("codigo")} ajuda={form.versao !== null ? "Não muda (é o tópico MQTT)" : undefined}>
                  <input id="mun-codigo" required pattern="[a-z0-9][a-z0-9_\-]{0,63}" value={form.codigo} readOnly={form.versao !== null} onBlur={() => tocar("codigo")}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value.trim().toLowerCase() })} placeholder="ex.: costarica" />
                </Campo>
                <Campo id="mun-nome" rotulo="Nome do município" erro={erro("nome")}>
                  <input id="mun-nome" required maxLength={80} value={form.nome} onBlur={() => tocar("nome")} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </Campo>
                <Campo id="mun-uf" rotulo="UF" erro={erro("uf")}>
                  <input id="mun-uf" size={3} maxLength={2} pattern="[A-Z]{2}" value={form.uf} onBlur={() => tocar("uf")}
                    onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
                </Campo>
                <Campo id="mun-ibge" rotulo="Código IBGE" erro={erro("ibge")}>
                  <input id="mun-ibge" size={8} pattern="[0-9]{7}" value={form.ibge} onBlur={() => tocar("ibge")}
                    onChange={(e) => setForm({ ...form, ibge: e.target.value.trim() })} />
                </Campo>
                <div className="tl-campo"><button type="button" className="nx-botao" onClick={() => void buscarIbge()}>Buscar no IBGE</button></div>
                {busca.length > 0 && (
                  <ul className="busca-ibge" aria-label="Resultado do IBGE">
                    {busca.map((b) => (
                      <li key={b.ibge}><button type="button" className="nx-botao botao-linha" onClick={() => { setForm({ ...form, nome: b.nome, uf: b.uf, ibge: b.ibge }); setBusca([]); }}>
                        {b.nome}/{b.uf} · {b.ibge}</button></li>
                    ))}
                  </ul>
                )}
                <Campo id="mun-filial" rotulo="Filial">
                  <select id="mun-filial" value={form.filial} onChange={(e) => setForm({ ...form, filial: e.target.value })}>
                    <option value="">—</option>{filiais.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Campo>
                <label className="opcao"><input type="checkbox" checked={form.exibir_cco} onChange={(e) => setForm({ ...form, exibir_cco: e.target.checked })} /> Exibir no CCO</label>
                <label className="opcao"><input type="checkbox" checked={form.simulado} onChange={(e) => setForm({ ...form, simulado: e.target.checked })} /> Simulação</label>
                <div className="tl-linha-acoes">
                  <button type="button" className="tl-fantasma" onClick={() => abrirForm(null)}>Cancelar</button>
                  <button type="submit" className="nx-botao nx-botao-principal">Salvar município</button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </section>
  );
}
